import React, { useState, useEffect, useRef } from 'react';
import { uploadFile, getUploadStatus } from '../services/api';

function FileUpload({ onUploadSuccess, crop }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [jobId, setJobId] = useState(null);
  const pollIntervalRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setMessage(null);
      setProgress(0);
    }
  };

  const pollUploadStatus = async (jobId) => {
    try {
      const status = await getUploadStatus(jobId);
      
      setProgress(status.progress);
      setMessage({ 
        type: 'info', 
        text: status.message || 'Processing...' 
      });
      
      if (status.status === 'completed') {
        setMessage({ 
          type: 'success', 
          text: status.message 
        });
        setUploading(false);
        setSelectedFile(null);
        setJobId(null);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        if (onUploadSuccess) onUploadSuccess();
      } else if (status.status === 'failed') {
        setMessage({ 
          type: 'error', 
          text: status.message || 'Upload failed' 
        });
        setUploading(false);
        setJobId(null);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Error polling status:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to check upload status. Please refresh and try again.' 
      });
      setUploading(false);
      setJobId(null);
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (jobId) {
      // Poll every 1 second
      pollIntervalRef.current = setInterval(() => {
        pollUploadStatus(jobId);
      }, 1000);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleUpload = async () => {
    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select a file first' });
      return;
    }

    setUploading(true);
    setProgress(0);
    setMessage(null);

    try {
      const result = await uploadFile(selectedFile, crop);
      setJobId(result.job_id);
      setMessage({ 
        type: 'info', 
        text: 'Upload started. Processing in background...' 
      });
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Failed to start upload';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setMessage({ 
        type: 'error', 
        text: errorMessage
      });
      setUploading(false);
    }
  };

  return (
    <div className="card">
      <h2>Upload Data File</h2>
      
      <div className="upload-section" onClick={() => !uploading && document.getElementById('fileInput').click()}>
        <input
          id="fileInput"
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileSelect}
          disabled={uploading}
          style={{ display: 'none' }}
        />
        <p>Click to select a CSV or XLSX file</p>
        {selectedFile && <p><strong>Selected:</strong> {selectedFile.name}</p>}
      </div>

      {uploading && (
        <div style={{ marginTop: '20px' }}>
          <div style={{
            width: '100%',
            height: '30px',
            backgroundColor: '#e0e0e0',
            borderRadius: '5px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              backgroundColor: '#025c4f',
              transition: 'width 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {progress}%
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={message.type} style={{ marginTop: '10px' }}>
          {message.text}
        </div>
      )}

      <button 
        className="btn" 
        onClick={handleUpload} 
        disabled={!selectedFile || uploading}
        style={{ marginTop: '20px' }}
      >
        {uploading ? 'Processing...' : 'Upload File'}
      </button>
    </div>
  );
}

export default FileUpload;
